/**
 *  @class
 *  @function BackgroundVideo
 */
if (!customElements.get('background-video')) {
	class BackgroundVideo extends HTMLElement {
		constructor() {
			super();

			this.tl = false;
			this.splittext = false;
			this.paused = false;
			this.muted = true;
			this.toggle = this.querySelector('.background-video__controls button');
			this.muteToggle = this.querySelector('.background-video__sound-controls button');
		}
		connectedCallback() {
			if (document.body.classList.contains('animations-true') && typeof gsap !== 'undefined') {
				this.prepareAnimations();
			}
			// Video Support.
			this.video_container = this.querySelector('.background-video__iframe');

			// Third-party iframes ship with data-src and are promoted to src after
			// the window load event. The onload handler that starts playback is
			// attached in promoteFrame() rather than here, so it binds to the frame
			// that actually receives a src.
			this.setupDeferredVideo();
			this.setupLightboxVideo();

			// Play/Pause control.
			if (this.toggle) {
				this.toggle.addEventListener('click', this.setupControlEvents.bind(this));
			}

			// Mute/Unmute control.
			if (this.muteToggle) {
				this.muteToggle.addEventListener('click', this.toggleMute.bind(this));
			}
		}
		disconnectedCallback() {
			if (document.body.classList.contains('animations-true') && typeof gsap !== 'undefined') {
				this.tl.kill();
				this.splittext.revert();
			}
		}

		/**
		 * Returns the iframe that is actually visible at the current breakpoint.
		 * skylight.scss hides either the desktop or the mobile iframe with
		 * display:none, so querySelector('iframe') alone would target the hidden
		 * one on mobile and the controls would appear to do nothing.
		 */
		activeFrame() {
			if (!this.video_container) return null;
			const frames = this.video_container.querySelectorAll('iframe');
			for (let i = 0; i < frames.length; i++) {
				if (window.getComputedStyle(frames[i]).display !== 'none') {
					return frames[i];
				}
			}
			return frames[0] || null;
		}

		/**
		 * Applies data-src to src for the visible iframe only, so a single player
		 * is ever fetched even when both a desktop and a mobile video are set.
		 * Note that display:none does not prevent an iframe loading, which is why
		 * the visibility check happens here rather than being left to CSS.
		 */
		promoteFrame() {
			if (!this.video_container) return false;

			const frames = this.video_container.querySelectorAll('iframe[data-src]');
			if (!frames.length) return false;

			let promoted = false;
			const _this = this;

			frames.forEach(function (frame) {
				if (window.getComputedStyle(frame).display === 'none') return;
				frame.onload = function () {
					_this.videoPlay();
				};
				frame.src = frame.dataset.src;
				frame.removeAttribute('data-src');
				promoted = true;
			});

			return promoted;
		}

		setupDeferredVideo() {
			if (!this.video_container) return;
			if (!this.video_container.querySelector('iframe[data-src]')) return;

			const _this = this;

			const start = function () {
				if (!('IntersectionObserver' in window)) {
					_this.promoteFrame();
					return;
				}
				const observer = new IntersectionObserver(function (entries) {
					for (let i = 0; i < entries.length; i++) {
						if (entries[i].isIntersecting) {
							_this.promoteFrame();
							observer.disconnect();
							return;
						}
					}
				}, { rootMargin: '200px' });
				observer.observe(_this);
			};

			// Re-check on breakpoint change so resizing across 767px still yields a
			// playing video rather than a bare fallback image.
			if (window.matchMedia) {
				const mq = window.matchMedia('(max-width: 767px)');
				const onChange = function () {
					_this.promoteFrame();
				};
				if (mq.addEventListener) {
					mq.addEventListener('change', onChange);
				} else if (mq.addListener) {
					mq.addListener(onChange);
				}
			}

			if (document.readyState === 'complete') {
				start();
			} else {
				window.addEventListener('load', start, { once: true });
			}
		}

		/**
		 * The lightbox player is only fetched when a visitor opens it.
		 */
		setupLightboxVideo() {
			const opener = this.querySelector('.video-lightbox-modal__opener button');
			const frame = this.querySelector('.video-lightbox-modal__content-info iframe[data-src]');
			if (!opener || !frame) return;

			opener.addEventListener('click', function () {
				frame.src = frame.dataset.src;
				frame.removeAttribute('data-src');
			}, { once: true });
		}

		setupControlEvents() {
			if (this.paused) {
				this.videoPlay();
				if (this.toggle) {
					this.toggle.classList.remove('paused');
				}
			} else {
				this.videoPause();
				if (this.toggle) {
					this.toggle.classList.add('paused');
				}
			}
		}
		videoPlay() {
			setTimeout(() => {
				const provider = this.video_container.dataset.provider;
				const iframe = this.activeFrame();
				const video = this.video_container.querySelector('video');

				if (provider === 'youtube' && iframe && iframe.contentWindow) {
					iframe.contentWindow.postMessage(JSON.stringify({
						event: "command",
						func: "playVideo",
						args: ""
					}), "*");
				} else if (provider === 'vimeo' && iframe && iframe.contentWindow) {
					iframe.contentWindow.postMessage(JSON.stringify({
						method: "play"
					}), "*");
				} else if (provider === 'hosted' && video) {
					video.play();
				}

				this.paused = false;
			}, 10);
		}
		videoPause() {
			setTimeout(() => {
				const provider = this.video_container.dataset.provider;
				const iframe = this.activeFrame();
				const video = this.video_container.querySelector('video');

				if (provider === 'youtube' && iframe && iframe.contentWindow) {
					iframe.contentWindow.postMessage(JSON.stringify({
						event: "command",
						func: "pauseVideo",
						args: ""
					}), "*");
				} else if (provider === 'vimeo' && iframe && iframe.contentWindow) {
					iframe.contentWindow.postMessage(JSON.stringify({
						method: "pause"
					}), "*");
				} else if (provider === 'hosted' && video) {
					video.pause();
				}
				this.paused = true;
			}, 10);
		}
		toggleMute() {
			if (this.muted) {
				this.videoUnmute();
				this.muteToggle.classList.add('is-unmuted');
				this.muteToggle.setAttribute('aria-label', 'Mute video');
			} else {
				this.videoMute();
				this.muteToggle.classList.remove('is-unmuted');
				this.muteToggle.setAttribute('aria-label', 'Unmute video');
			}
		}
		videoUnmute() {
			const provider = this.video_container.dataset.provider;
			const iframe = this.activeFrame();
			const video = this.video_container.querySelector('video');

			if (provider === 'youtube' && iframe && iframe.contentWindow) {
				iframe.contentWindow.postMessage(JSON.stringify({
					event: "command",
					func: "unMute",
					args: ""
				}), "*");
			} else if (provider === 'vimeo' && iframe && iframe.contentWindow) {
				iframe.contentWindow.postMessage(JSON.stringify({
					method: "setVolume",
					value: 1
				}), "*");
			} else if (provider === 'hosted' && video) {
				video.muted = false;
			}
			this.muted = false;
		}
		videoMute() {
			const provider = this.video_container.dataset.provider;
			const iframe = this.activeFrame();
			const video = this.video_container.querySelector('video');

			if (provider === 'youtube' && iframe && iframe.contentWindow) {
				iframe.contentWindow.postMessage(JSON.stringify({
					event: "command",
					func: "mute",
					args: ""
				}), "*");
			} else if (provider === 'vimeo' && iframe && iframe.contentWindow) {
				iframe.contentWindow.postMessage(JSON.stringify({
					method: "setVolume",
					value: 0
				}), "*");
			} else if (provider === 'hosted' && video) {
				video.muted = true;
			}
			this.muted = true;
		}
		prepareAnimations() {
			let section = this;

			section.tl = gsap.timeline({
				scrollTrigger: {
					trigger: section,
					start: "top center"
				}
			});

			document.fonts.ready.then(function () {
				gsap.set(section.querySelectorAll('.subheading, .background-video__content--heading, .rte p, .video-lightbox-modal__opener button'), { visibility: 'visible' });
				section.splittext = new SplitText(section.querySelectorAll('.background-video__content--heading, .rte p'), {
					type: 'lines',
					linesClass: 'line-child'
				});
				section.mask = new SplitText(section.querySelectorAll('.background-video__content--heading,  .rte p'), {
					type: 'lines',
					linesClass: 'line-parent'
				});

				section.tl
					.fromTo(section.querySelector('.subheading'), {
						opacity: 0
					}, {
						duration: 0.25,
						opacity: 1
					});
				section.tl
					.from(section.querySelectorAll('.background-video__content--heading .line-child'), {
						duration: 0.75,
						yPercent: 120,
						stagger: 0.1,
						rotation: '2deg'
					}, '>-=0.3');
				section.tl
					.from(section.querySelectorAll('.rte p .line-child'), {
						duration: 0.5,
						yPercent: 120,
						stagger: 0.02,
						rotation: '2deg'
					}, '>-=0.3');
				section.tl
					.fromTo(section.querySelector('.video-lightbox-modal__opener button'), {
						autoAlpha: 0
					}, {
						duration: 0.5,
						autoAlpha: 1
					}, '>-=0.3');

			});
		}
	}
	customElements.define('background-video', BackgroundVideo);
}
